import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Progress,
  Stack,
  VStack,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { OtpInput } from '@common/elements/OtpInput';
import { isValidEmail, isValidPassword } from '@common/utility/validations';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from 'src/apps/invest/config';
import { useSignInViewModel } from '../../models/SignInViewModel';

export const ResetPaswordForm: React.FC = () => {
  const [viewState, setViewState] = useState<'email' | 'code-and-new-password' | 'success' | 'loading'>('email');
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { requestPasswordReset, verifyPasswordResetCode } = useSignInViewModel();
  const emailForm = useForm<{ email: string }>();
  const codePasswordForm = useForm<{ code: string; password: string }>();
  const responseDelay = 1500; // slow down feedback to avoid flashing

  const handleEmailFormSubmission = emailForm.handleSubmit(async (data) => {
    setViewState('loading');
    setLoadingMessage('Sending password reset code...');
    const result = await requestPasswordReset(data.email);
    if (result.error) {
      emailForm.setError('email', { message: result.error });
      setTimeout(() => setViewState('email'), responseDelay);
    } else {
      setTimeout(() => setViewState('code-and-new-password'), responseDelay);
    }
  });

  const handleCodePasswordFormSubmission = codePasswordForm.handleSubmit(async (data) => {
    setViewState('loading');
    setLoadingMessage('Verifying code and resetting password...');
    const result = await verifyPasswordResetCode(emailForm.getValues('email'), data.code, data.password);
    if (result.error) {
      codePasswordForm.setError('code', { message: result.error });
      setTimeout(() => setViewState('code-and-new-password'), responseDelay);
    } else {
      setTimeout(() => setViewState('success'), responseDelay);
    }
  });

  const EmailView = () => (
    <VStack gap={10}>
      <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
        <Heading fontSize={'21px'}>Reset Password</Heading>
        <TextBlock color="muted">Tell us your e-mail address and we'll send you a password reset code.</TextBlock>
      </Stack>
      <form onSubmit={handleEmailFormSubmission} style={{ width: '100%' }}>
        <Stack spacing={5}>
          <FormControl isInvalid={Boolean(emailForm.formState.errors.email)}>
            <Input
              id="email"
              placeholder="Email"
              {...emailForm.register('email', {
                required: 'Please enter an email',
                validate: (v) => isValidEmail(v) || 'Please use a valid email address',
              })}
              autoFocus={true}
            />
            <FormErrorMessage>{emailForm.formState.errors.email?.message}</FormErrorMessage>
          </FormControl>
          <Button
            isDisabled={Boolean(emailForm.formState.errors.email) || viewState === 'loading'}
            isLoading={viewState === 'loading'}
            type="submit">
            Continue
          </Button>
          <Button variant="link" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Stack>
      </form>
    </VStack>
  );

  const NewCodeAndPasswordView = () => (
    <VStack gap={10}>
      <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
        <Heading>New Password</Heading>
        <TextBlock color="muted">
          We just sent you a text message with a verification code. Enter the code and your new password below.
        </TextBlock>
      </Stack>

      <form onSubmit={handleCodePasswordFormSubmission} style={{ width: '100%' }}>
        <Stack spacing={5}>
          <Stack w="full">
            <FormControl isInvalid={Boolean(codePasswordForm.formState.errors.code)}>
              <FormLabel htmlFor="password">Code</FormLabel>

              <HStack my={3}>
                <Controller
                  control={codePasswordForm.control}
                  name="code"
                  rules={{
                    maxLength: 6,
                    minLength: 6,
                    required: 'Pin is required',
                  }}
                  render={({ field }) => <OtpInput onChange={field.onChange} otp={field.value} />}
                />
                <Button variant="link" size="sm" onClick={() => codePasswordForm.setValue('code', '')}>
                  clear
                </Button>
              </HStack>

              <FormErrorMessage>
                {codePasswordForm.formState.errors.code && codePasswordForm.formState.errors.code.message}
              </FormErrorMessage>
            </FormControl>
          </Stack>

          <FormControl isInvalid={Boolean(codePasswordForm.formState.errors.password)}>
            <FormLabel htmlFor="password">New Password</FormLabel>
            <InputGroup>
              <Input
                autoComplete="off"
                id="password"
                tabIndex={7}
                type={showPassword ? 'text' : 'password'}
                placeholder="********"
                {...codePasswordForm.register('password', {
                  minLength: { message: 'Password must be at least 8 characters', value: 8 },
                  required: 'Password is required',
                  validate: (v) =>
                    isValidPassword(v) ||
                    'Password must be at least 8 characters, contain a number, and a special character',
                })}
                autoFocus={codePasswordForm.formState.errors.password ? true : false}
              />
              <InputRightElement mx="1.5">
                <IconButton
                  aria-label="Show hide password"
                  bg="transparent"
                  _hover={{ bg: 'transparent' }}
                  _active={{ bg: 'transparent' }}
                  color="grey"
                  onClick={() => setShowPassword(!showPassword)}
                  icon={<Icon icon={showPassword ? 'eye' : 'eye-slash'} />}
                />
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{codePasswordForm.formState.errors.password?.message}</FormErrorMessage>
          </FormControl>

          <Button
            isDisabled={
              Boolean(codePasswordForm.formState.errors.code) ||
              Boolean(codePasswordForm.formState.errors.password) ||
              viewState === 'loading'
            }
            isLoading={viewState === 'loading'}
            tabIndex={8}
            type="submit">
            Change Password
          </Button>
          <Button variant="link" onClick={() => navigate(-1)} tabIndex={9}>
            Cancel
          </Button>
        </Stack>
      </form>
    </VStack>
  );

  const SuccessView = () => (
    <VStack gap={10}>
      <Stack spacing={{ base: '2', md: '5' }} textAlign="center">
        <Icon icon="circle-check" color="green" boxSize={20} alignSelf="center" />
        <Heading>Password Changed</Heading>
        <TextBlock color="muted">
          You've successfully changed your password. You can now sign in with your new password.
        </TextBlock>
        <Button as={Link} to={ROUTES.signin}>
          Sign In
        </Button>
      </Stack>
    </VStack>
  );

  const LoadingView = () => (
    <Stack spacing={{ base: '2', md: '10' }} textAlign="center">
      <Heading fontSize={'2xl'}>{loadingMessage ?? 'Processing...'}</Heading>
      <Progress isIndeterminate size="xs" />
      <Button variant="link" as={Link} to={ROUTES.signin}>
        Cancel
      </Button>
    </Stack>
  );

  switch (viewState) {
    case 'loading':
      return <LoadingView />;
    case 'code-and-new-password':
      return <NewCodeAndPasswordView />;
    case 'email':
      return <EmailView />;
    case 'success':
      return <SuccessView />;
    default:
      return <EmailView />;
  }
};
